import axios from 'axios';
import { clearSageSession } from '../utils/sageSession';

const BASE_PREFIX = window.__BASE_PATH__ && window.__BASE_PATH__ !== '/' ? window.__BASE_PATH__ : '';
const API_BASE_URL = `${BASE_PREFIX}${import.meta.env.VITE_API_URL || '/api'}`;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token getter set by AuthGuard
let _getAccessToken = null;
let _currentScreenName = null;
let _tokenReadyResolve = null;
const _tokenReadyPromise = new Promise((resolve) => {
  _tokenReadyResolve = resolve;
});

export const setAccessTokenGetter = (getter) => {
  _getAccessToken = getter;
  if (_tokenReadyResolve) {
    _tokenReadyResolve();
  }
};

// Wait for token getter to be set (used by interceptor)
const waitForTokenGetter = () => {
  if (_getAccessToken) return Promise.resolve();
  return _tokenReadyPromise;
};

export const setCurrentScreenName = (screenName) => {
  _currentScreenName = screenName;
};

// Attach JWT token and screen name to every request
api.interceptors.request.use(
  async (config) => {
    // For non-auth endpoints, wait for token getter to be set (with timeout)
    const isAuthEndpoint = config.url?.includes('/auth/');
    if (!isAuthEndpoint && !_getAccessToken) {
      await Promise.race([
        waitForTokenGetter(),
        new Promise(resolve => setTimeout(resolve, 5000))
      ]);
    }

    // Always try to attach token if getter is available
    if (_getAccessToken) {
      try {
        const token = await _getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (err) {
        console.warn('Failed to get access token:', err.message);
      }
    }
    // Add screen name for audit logging
    if (_currentScreenName) {
      config.headers['X-Screen-Name'] = _currentScreenName;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => (response.config?._bypassUnwrap ? response : response.data),
  (error) => {
    if (error.response?.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    const data = error.response?.data;
    // Prefer message over error: errorHandler.js's generic path sets
    // error to err.name (always the literal string "Error") and message
    // to the actual, specific reason — showing error first meant every
    // sweepstakes rejection (not a winner, already paid, in flight) just
    // displayed the word "Error" on screen. Verified this ordering holds
    // everywhere else too — error is always the short generic label,
    // message the specific user-facing text — so this is a strict
    // improvement, not just a sweepstakes-scoped change.
    const message =
      data?.message || data?.error || error.message || 'An error occurred';
    console.error('API Error:', message, data?.details || '');
    const wrapped = new Error(message);
    wrapped.response = error.response;
    wrapped.details = data?.details;
    return Promise.reject(wrapped);
  }
);

export const reportsApi = {
  getSalesSummary: (startDate, endDate) =>
    api.get('/reports/sales-summary', { params: { startDate, endDate } }),

  getTopProducts: (limit = 10) =>
    api.get('/reports/top-products', { params: { limit } }),

  getRegionalPerformance: () =>
    api.get('/reports/regional-performance'),

  getRevenueByCategory: () =>
    api.get('/reports/revenue-by-category'),

  getKPIMetrics: () =>
    api.get('/reports/kpi-metrics'),

  getTrendData: (metric = 'revenue', period = 'daily') =>
    api.get('/reports/trends', { params: { metric, period } }),

  executeCustomQuery: (query) =>
    api.post('/reports/custom-query', { query }),

  getTenantAdoptionReport: (startDate, endDate) =>
    api.get('/reports/tenant-adoption', { params: { startDate, endDate } }),

  getTenantAdoptionToday: () =>
    api.get('/reports/tenant-adoption/today'),

  getTenantAdoptionSummary: () =>
    api.get('/reports/tenant-adoption/summary'),

  getCardMetricsReport: (startDate, endDate) =>
    api.get('/reports/card-metrics', { params: { startDate, endDate } }),

  getCardMetricsToday: () =>
    api.get('/reports/card-metrics/today'),

  getCardMetricsSummary: () =>
    api.get('/reports/card-metrics/summary'),

  searchConsumer: (searchTerm) =>
    api.get('/reports/consumer/search', { params: { searchTerm } }),

  getConsumerEnrollmentHistory: (memNbr) =>
    api.get('/reports/consumer/enrollment-history', { params: { memNbr } }),

  getConsumerLoginHistory: (consumerId) =>
    api.get('/reports/consumer/login-history', { params: { consumerId } }),

  getCardValueLoads: (consumerCode) =>
    api.get('/reports/consumer/card-value-loads', { params: { consumerCode } }),

  getCardProxy: (consumerCode) =>
    api.get('/reports/consumer/card-proxy', { params: { consumerCode } }),

  getConsumerPerson: (consumerCode) =>
    api.get('/reports/consumer/person', { params: { consumerCode } }),

  getConsumerCohort: (consumerCode) =>
    api.get('/reports/consumer/cohort', { params: { consumerCode } }),

  getConsumerWallet: (consumerCode) =>
    api.get('/reports/consumer/wallet', { params: { consumerCode } }),

  getCardTransactions: (consumerCode) =>
    api.get('/reports/consumer/card-transactions', { params: { consumerCode } }),

  getFISAddress: (cardNumberProxy) =>
    api.get('/reports/consumer/fis-address', { params: { cardNumberProxy } }),

  getTransactionItemDetails: (txnUid) =>
    api.get('/reports/consumer/transaction-item-details', { params: { txnUid } }),

  getMonetaryTransactionDetails: (txnUid) =>
    api.get('/reports/consumer/monetary-transaction-details', { params: { txnUid } }),

  getDeclinedTransactions: (cardProxy) =>
    api.get('/reports/consumer/declined-transactions', { params: { cardProxy } }),

  getDeclinedTransactionDetails: (txnUid, cardProxy) =>
    api.get('/reports/consumer/declined-transaction-details', { params: { txnUid, cardProxy } }),

  getPendingTransactions: (cardProxy) =>
    api.get('/reports/consumer/pending-transactions', { params: { cardProxy } }),

  getTransactionSpendCategories: (txnUid) =>
    api.get('/reports/consumer/transaction-spend-categories', { params: { txnUid } }),

  getDailyCallCenterHealthSummary: () =>
    api.get('/reports/call-center/daily-health-summary'),

  getAgentProductivityDaily: () =>
    api.get('/reports/call-center/agent-productivity-daily'),

  getConsumerRewardsHistory: (consumerCode) =>
    api.get('/reports/consumer/rewards-history', { params: { consumerCode } }),

  getConsumerTasks: (consumerCode) =>
    api.get('/reports/consumer/tasks', { params: { consumerCode } }),

  // Consolidated consumer summary - all data in one call
  getConsumerSummaryConsolidated: (searchTerm) =>
    api.get('/reports/consumer/summary', { params: { searchTerm } }),

  getCallIntentPatterns: () =>
    api.get('/reports/call-center/intent-patterns'),

  getCallIntentTrends: () =>
    api.get('/reports/call-center/intent-trends'),

  getCaseStatistics: () =>
    api.get('/reports/call-center/case-statistics'),

  getCaseTypeDistribution: () =>
    api.get('/reports/call-center/case-type-distribution'),

  getCaseReasonDistribution: () =>
    api.get('/reports/call-center/case-reason-distribution'),

  analyzeCasePatterns: () =>
    api.get('/reports/call-center/case-patterns-ai', { timeout: 120000 }),

  getCasePatternsSql: () =>
    api.get('/reports/call-center/case-patterns-sql'),

  generateCard35File: (query, preview = false) =>
    api.post('/reports/fis/generate-card35', { query, preview }),

  generateCard30File: (members, preview = false, actionType = '01', testProdIndicator = 'P') =>
    api.post('/reports/fis/generate-card30', { members, preview, actionType, testProdIndicator }),

  searchAplProducts: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        if (value.length > 0) params.append(key, value.join(','));
      } else if (value !== '') {
        params.append(key, value);
      }
    });
    return api.get(`/reports/fis/apl/search?${params.toString()}`);
  },

  getAplFilterOptions: () => api.get('/reports/fis/apl/filters'),

  searchMerchantNetwork: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach(v => params.append(key, v));
      } else if (value !== '') {
        params.append(key, value);
      }
    });
    return api.get(`/reports/fis/merchant-network/search?${params.toString()}`);
  },

  getMerchantFilterOptions: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach(v => params.append(key, v));
      } else if (value !== '') {
        params.append(key, value);
      }
    });
    const qs = params.toString();
    return api.get(`/reports/fis/merchant-network/filters${qs ? `?${qs}` : ''}`);
  },

  runEnrollmentCheck: (checkId) =>
    api.post('/reports/enrollment/run-check', { checkId }),

  getCardTransactionPatterns: () =>
    api.get('/reports/card-metrics/transaction-patterns'),

  getWalletTransactionDetails: () =>
    api.get('/reports/card-metrics/wallet-transaction-details'),

  getReasonCodeDistribution: () =>
    api.get('/reports/card-metrics/reason-code-distribution'),

  getMerchantsByReasonCode: (reasonCode) =>
    api.get('/reports/card-metrics/merchants-by-reason-code', { params: { reasonCode } }),

  // Consumer Financial Transactions
  getConsumerTransactions: (params) =>
    api.get('/reports/consumer-transactions', { params }),

  getConsumerTransactionSummary: (params) =>
    api.get('/reports/consumer-transactions/summary', { params }),

  getConsumerTransactionItemDetails: (params) =>
    api.get('/reports/consumer-transactions/item-details', { params }),

  // Health Metrics
  getHealthMetricsSummary: () =>
    api.get('/reports/health-metrics/summary'),

  getHealthMetricsDaily: (days = 30) =>
    api.get('/reports/health-metrics/daily', { params: { days } }),

  getHealthMetricsByTenant: (days = 30) =>
    api.get('/reports/health-metrics/by-tenant', { params: { days } }),

  getHealthMetricTypes: () =>
    api.get('/reports/health-metrics/types'),

  getHealthMetricsToday: () =>
    api.get('/reports/health-metrics/today'),

  getHealthMetricsRollup: () =>
    api.get('/reports/health-metrics/rollup'),
};

export const authApi = {
  syncUser: (userData) =>
    api.post('/auth/sync', userData),

  getProfile: () =>
    api.get('/auth/me'),

  getNavigation: () =>
    api.get('/auth/navigation'),

  getPermissions: () =>
    api.get('/auth/permissions'),
};

export const adminApi = {
  // Users
  listUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  updateUserRoles: (userId, roleIds) => api.put(`/admin/users/${userId}/roles`, { roleIds }),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),

  // Roles
  listRoles: () => api.get('/admin/roles'),
  createRole: (data) => api.post('/admin/roles', data),
  updateRole: (roleId, data) => api.put(`/admin/roles/${roleId}`, data),
  updateRolePermissions: (roleId, permissionIds) =>
    api.put(`/admin/roles/${roleId}/permissions`, { permissionIds }),

  // Permissions
  listPermissions: () => api.get('/admin/permissions'),

  // Audit
  getAuditLog: (limit = 50) => api.get('/admin/audit-log', { params: { limit } }),
};

export const sweepstakesApi = {
  listClients: () =>
    api.get('/sweepstakes/clients'),

  previewReport: ({ client, month }) =>
    api.post('/sweepstakes/report/preview', { client, month }),

  downloadReport: async ({ client, month }) => {
    // Bypass the global response interceptor so we can read headers (S3 URI,
    // Glue run id) alongside the blob body.
    const response = await api.post(
      '/sweepstakes/report',
      { client, month },
      {
        responseType: 'blob',
        transformResponse: (x) => x,
        _bypassUnwrap: true,
      }
    );
    return {
      blob: response.data,
      s3Uri: response.headers['x-sweepstakes-s3-uri'] || null,
      s3Mocked: response.headers['x-sweepstakes-s3-mocked'] === 'true',
      glueJobRunId: response.headers['x-sweepstakes-glue-run-id'] || null,
      glueError: response.headers['x-sweepstakes-glue-error'] || null,
    };
  },

  previewWinners: ({ client, month, file }) => {
    const formData = new FormData();
    formData.append('client', client);
    formData.append('month', month);
    formData.append('file', file);
    return api.post('/sweepstakes/winners/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  processValueLoad: ({ client, month, anonymousCode, consumerCode }) =>
    api.post('/sweepstakes/winners/value-load', { client, month, anonymousCode, consumerCode }),

  processCardStatus: ({ client, anonymousCode, consumerCode, targetStatus }) =>
    api.post('/sweepstakes/winners/card-status', { client, anonymousCode, consumerCode, targetStatus }),

  uploadWinners: ({ client, month, file }) => {
    const formData = new FormData();
    formData.append('client', client);
    formData.append('month', month);
    formData.append('file', file);
    return api.post('/sweepstakes/winners', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const healthApi = {
  check: () => api.get('/health'),
  ready: () => api.get('/health/ready'),
  live: () => api.get('/health/live'),
};

// Perf dashboard SPA HTML (served from S3 by the backend). Direct-URL page, not in the sidebar.
export const perfDashboardApi = {
  get: () => api.get('/perf-dashboard'),
};

export const jiraApi = {
  testConnection: () =>
    api.get('/jira/test-connection'),

  getTeamsConfig: () =>
    api.get('/jira/teams'),

  getExecutiveDashboard: (filters = {}) =>
    api.get('/jira/executive-dashboard', {
      timeout: 120000,
      params: {
        startDate: filters.startDate,
        endDate: filters.endDate,
      },
    }),

  getSprintTeamMetrics: (teamKey, filters = {}) =>
    api.get(`/jira/sprint/${teamKey}`, {
      timeout: 60000,
      params: {
        startDate: filters.startDate,
        endDate: filters.endDate,
        sprintIds: filters.sprintIds?.join(','),
      },
    }),

  getKanbanTeamMetrics: (teamKey, filters = {}) =>
    api.get(`/jira/kanban/${teamKey}`, {
      timeout: 60000,
      params: {
        startDate: filters.startDate,
        endDate: filters.endDate,
      },
    }),

  getBoardSprints: (boardId, state = 'active,closed') =>
    api.get(`/jira/board/${boardId}/sprints`, { params: { state } }),

  getSprintIssues: (sprintId) =>
    api.get(`/jira/sprint-issues/${sprintId}`),

  getBoardIssues: (boardId, jql = '') =>
    api.get(`/jira/board/${boardId}/issues`, { params: { jql } }),

  getVelocityChart: (boardId) =>
    api.get(`/jira/board/${boardId}/velocity`),

  searchIssues: (jql, fields) =>
    api.get('/jira/search', { params: { jql, fields } }),

  getIssuePatterns: (teamKey, options = {}) =>
    api.get(`/jira/patterns/${teamKey}`, {
      timeout: 120000, // 2 minutes - pattern analysis can be slow
      params: {
        startDate: options.startDate,
        endDate: options.endDate,
      },
    }),
};

export const releaseReadinessApi = {
  getReport: ({ fixVersion, releaseBranch, projectKey = 'ADM', sprintId } = {}) =>
    api.get('/release-readiness', {
      timeout: 60000,
      params: { fixVersion, releaseBranch, projectKey, ...(sprintId ? { sprintId } : {}) },
    }),

  // Re-runs the report server-side and writes its snapshot to the release's Confluence page.
  // A POST (not a side effect of getReport) because it creates/updates real pages, and it needs
  // the release-readiness:publish permission.
  publish: ({ fixVersion, releaseBranch, projectKey = 'ADM', sprintId } = {}) =>
    api.post(
      '/release-readiness/publish',
      { fixVersion, releaseBranch, projectKey, ...(sprintId ? { sprintId } : {}) },
      { timeout: 60000 },
    ),

  getSprints: (projectKey = 'ADM') =>
    api.get('/release-readiness/sprints', { params: { projectKey } }),
};

export const logsApi = {
  getLogGroups: () => api.get('/logs/groups'),
  simpleSearch: (params) => api.post('/logs/simple-search', params, { timeout: 60000 }),
  runInsightsQuery: (params) => api.post('/logs/insights-query', params, { timeout: 90000 }),
  analyzePatterns: (params, signal) => api.post('/logs/patterns', params, { timeout: 180000, signal }),
};

export const sprintGoalsApi = {
  // Get goals for a sprint (with mapped story keys)
  getGoals: (boardId, sprintId) =>
    api.get(`/sprint-goals/${boardId}/${sprintId}`),

  // Get mapped issue keys for a sprint (helper)
  getMappedIssueKeys: (boardId, sprintId) =>
    api.get(`/sprint-goals/${boardId}/${sprintId}/mapped-issues`),

  // Create a new goal
  createGoal: (data) =>
    api.post('/sprint-goals', data),

  // Update goal details
  updateGoal: (goalId, data) =>
    api.put(`/sprint-goals/${goalId}`, data),

  // Delete a goal
  deleteGoal: (goalId) =>
    api.delete(`/sprint-goals/${goalId}`),

  // Add a story to a goal
  addStory: (goalId, issueKey) =>
    api.post(`/sprint-goals/${goalId}/stories`, { issueKey }),

  // Remove a story from a goal
  removeStory: (goalId, issueKey) =>
    api.delete(`/sprint-goals/${goalId}/stories/${issueKey}`),

  // Bulk update stories for a goal
  updateGoalStories: (goalId, issueKeys) =>
    api.put(`/sprint-goals/${goalId}/stories`, { issueKeys }),

  // Reorder goals within a sprint
  reorderGoals: (boardId, sprintId, goalIds) =>
    api.put(`/sprint-goals/${boardId}/${sprintId}/reorder`, { goalIds }),
};

// ─── AWS CONNECT API ────────────────────────────────────────

export const connectApi = {
  // Get live queue metrics
  getLiveQueueMetrics: (instanceId, queueIds, region = 'us-east-1') =>
    api.get('/connect/live-queue-metrics', {
      params: {
        instance_id: instanceId,
        queue_ids: queueIds.join(','),
        region,
      },
    }),

  // Clear metrics cache
  clearCache: () => api.post('/connect/clear-cache'),
};

// ─── ELIGIBILITY API ────────────────────────────────────────

export const eligibilityApi = {
  // Get all mapping configuration names
  getMappingConfigs: () => api.get('/eligibility/mapping-configs'),

  // Get column mappings for a configuration
  getColumnMappings: (configName) =>
    api.get(`/eligibility/mappings/${encodeURIComponent(configName)}`),

  // Get source table columns (member_import_file_data)
  getSourceColumns: () => api.get('/eligibility/source-columns'),

  // Get target table columns (huser.consumer)
  getTargetColumns: () => api.get('/eligibility/target-columns'),

  // Save column mappings
  saveMappings: (mappingName, mappings) =>
    api.post('/eligibility/mappings', { mappingName, mappings }),

  // Delete a mapping configuration
  deleteMappingConfig: (configName) =>
    api.delete(`/eligibility/mappings/${encodeURIComponent(configName)}`),

  // Get latest member records
  getLatestMembers: (limit = 100, offset = 0) =>
    api.get('/eligibility/latest-members', { params: { limit, offset } }),

  // Run eligibility comparison
  compare: (mappingName, limit = 100, mismatchesOnly = false) =>
    api.post('/eligibility/compare', { mappingName, limit, mismatchesOnly }),
};

// ─── ELIGIBILITY DATA API ────────────────────────────────────────

export const eligibilityDataApi = {
  // Search member import file data
  search: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    return api.get(`/eligibility-data/search?${params.toString()}`);
  },

  // Get filter options for dropdowns
  getFilterOptions: () => api.get('/eligibility-data/filters'),

  // Get single record by ID
  getById: (id) => api.get(`/eligibility-data/${id}`),
};

// ─── FILE INTEGRITY API ──────────────────────────────────

export const fileIntegrityApi = {
  getDagCounts: () => api.get('/file-integrity/dag-counts'),
};

// ─── CARD STATUS API ─────────────────────────────────────

export const cardStatusApi = {
  getHistory: (consumerCode, tenantCode, cardProxy) =>
    api.get('/card-status/history', { params: { consumerCode, tenantCode, cardProxy } }),
};

// ─── PROGRAM DESIGN API ─────────────────────────────────

export const programDesignApi = {
  getCatalog: () => api.get('/program-design/catalog'),
};

// ─── AUDIT API ────────────────────────────────────────

export const auditApi = {
  // Get audit logs with filters
  getLogs: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    return api.get(`/audit/logs?${params.toString()}`);
  },

  // Get single audit log by ID (full details)
  getLogById: (id) => api.get(`/audit/logs/${id}`),

  // Get audit summary statistics
  getSummary: (startDate, endDate) =>
    api.get('/audit/summary', { params: { startDate, endDate } }),

  // Get filter options for dropdowns
  getFilterOptions: () => api.get('/audit/filters'),

  // Export audit logs
  exportLogs: (filters, format = 'csv') =>
    api.post('/audit/export', { filters, format }, { responseType: 'blob' }),

  // Log a frontend event (page view, action, etc.)
  logEvent: (eventData) =>
    api.post('/audit/log', eventData),
};

// ─── PREFUNDING API ────────────────────────────────────────

export const prefundingApi = {
  // Run the prefunding report (longer timeout for complex query)
  runReport: (tenantCode, runAsOf) =>
    api.post('/prefunding/run', { tenantCode, runAsOf }, { timeout: 120000 }),

  // Get list of tenants for dropdown
  getTenants: () => api.get('/prefunding/tenants'),

  // Save report results to S3 (uses cached data on backend)
  saveReport: (runId) =>
    api.post('/prefunding/save', { runId }, { timeout: 120000 }),

  // Get run history for compare dropdown
  getRunHistory: (tenantCode) =>
    api.get(`/prefunding/run-history/${encodeURIComponent(tenantCode)}`),

  // Run compare against previous run
  runCompare: (tenantCode, runAsOf, previousRunId) =>
    api.post('/prefunding/compare', { tenantCode, runAsOf, previousRunId }, { timeout: 180000 }),

  // Get records by run ID (for update status feature)
  getRecordsByRunId: (tenantCode, runId) =>
    api.get(`/prefunding/records/${encodeURIComponent(tenantCode)}/${encodeURIComponent(runId)}`, { timeout: 120000 }),

  // Update approval status and export to S3
  updateApprovalStatus: (tenantCode, runId, updates, matchBy = 'consumer_code', fullRecords = []) =>
    api.post('/prefunding/update-status', { tenantCode, runId, updates, matchBy, fullRecords }, { timeout: 120000 }),
};

// ─── REWARD INTEGRITY CHECKS API ─────────────────────────────

export const rewardIntegrityApi = {
  // Finds consumers paid the same reward more than once in the same period
  runDuplicateRewardCheck: (lookbackDays, dedupBaselineStart) =>
    api.post('/reward-integrity/duplicate-reward-check', { lookbackDays, dedupBaselineStart }, { timeout: 120000 }),

  // Reconciles task completions against wallet transactions for a single day
  runTaskWalletReconciliation: (checkDate) =>
    api.post('/reward-integrity/task-wallet-reconciliation', { checkDate }, { timeout: 120000 }),
};

// ─── SAVED QUERIES API ────────────────────────────────────────

export const savedQueriesApi = {
  // Get all saved queries for the current user
  getSavedQueries: () => api.get('/queries/saved'),

  // Save a new query
  saveQuery: (queryName, queryText, description = null, isPublic = false) =>
    api.post('/queries/saved', { queryName, queryText, description, isPublic }),

  // Update an existing query
  updateQuery: (queryId, updates) =>
    api.put(`/queries/saved/${queryId}`, updates),

  // Delete a saved query
  deleteQuery: (queryId) =>
    api.delete(`/queries/saved/${queryId}`),
};

// ─── RECONCILIATION API ────────────────────────────────────────

// ─── SAGE AI ASSISTANT API ────────────────────────────────────────

export const sageApi = {
  // SSE streaming question — uses raw fetch (not axios) for SSE support.
  // `messages` is the client-authoritative conversation history (Option C):
  // the full Anthropic-format array returned by the previous response. Sent
  // back on each request so context survives refreshes, pod restarts, and
  // rolling deploys. Pass null/undefined on the first request of a thread.
  askStream: async (question, threadId, messages, { onToken, onDone, onError }, signal) => {
    let token;
    if (_getAccessToken) {
      try { token = await _getAccessToken(); } catch (_) {}
    }

    const res = await fetch(`${API_BASE_URL}/sage/chat`, {
      method: 'POST',
      // AbortController signal: New Chat aborts an in-flight stream so its
      // onDone never fires and can't resurrect a reset conversation.
      signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(_currentScreenName ? { 'X-Screen-Name': _currentScreenName } : {}),
      },
      body: JSON.stringify({
        question,
        thread_id: threadId,
        ...(messages ? { messages } : {}),
      }),
    });

    if (!res.ok) {
      // Session expired mid-request: this raw fetch bypasses the axios 401
      // interceptor, so clear the persisted conversation here and fire the
      // same auth:unauthorized event AuthGuard listens for. Without this a
      // prior user's transcript/history would survive into the next login in
      // the same tab (PII leak on shared computers).
      if (res.status === 401 || res.status === 403) {
        clearSageSession();
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        // Pass the 'auth_error' code so the panel replaces (not annotates) the
        // transcript. Without a code the panel keeps the old messages in state
        // and its persist-on-settle effect re-writes them to SS_DISPLAY_MESSAGES,
        // undoing the clear above and leaking the prior user's conversation into
        // the next login in the same tab.
        onError('Your session has expired. Please sign in again.', 'auth_error');
        return;
      }
      onError('Failed to connect to SAGE. Please try again.');
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // Intentionally NO try/catch around this loop. When New Chat aborts the
    // signal mid-stream, reader.read() rejects with an AbortError that must
    // propagate out of askStream and up to handleSend's catch in the panel,
    // where it is recognized as an intentional cancel. Wrapping the loop in a
    // try/catch would swallow that rejection and silently break the abort fix
    // for its most common case (New Chat while tokens are still arriving). The
    // inner try below only guards JSON.parse of individual SSE data lines.
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      let eventType = null;
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          eventType = line.slice(7).trim();
        } else if (line.startsWith('data: ') && eventType) {
          try {
            const data = JSON.parse(line.slice(6));
            if (eventType === 'token') onToken(data.chunk || '');
            else if (eventType === 'done') onDone(data);
            else if (eventType === 'error') onError(data.error || 'Unknown error', data.code);
          } catch (_) {}
          eventType = null;
        }
      }
    }
  },

  clearThread: (threadId) =>
    api.delete(`/sage/thread/${threadId}`),

  health: () =>
    api.get('/sage/health'),
};

export const reconciliationApi = {
  // Get forecast summary by tenant/task
  getForecast: (sourceMonth, forecastMonth) =>
    api.get('/reconciliation/forecast', { params: { sourceMonth, forecastMonth } }),

  // Get detailed consumer list for a task
  getForecastDetail: (sourceMonth, forecastMonth, tenantCode, taskCode) =>
    api.get('/reconciliation/forecast-detail', { params: { sourceMonth, forecastMonth, tenantCode, taskCode } }),

  // Get forecast summary by tenant
  getForecastByTenant: (sourceMonth, forecastMonth) =>
    api.get('/reconciliation/forecast-by-tenant', { params: { sourceMonth, forecastMonth } }),
};

export const reconRulesApi = {
  // Rules
  listRules: (type) => api.get('/recon-rules/rules', { params: { type } }),
  getRule: (ruleId) => api.get(`/recon-rules/rules/${ruleId}`),
  createRule: (data) => api.post('/recon-rules/rules', data),
  updateRule: (ruleId, data) => api.put(`/recon-rules/rules/${ruleId}`, data),
  deleteRule: (ruleId) => api.delete(`/recon-rules/rules/${ruleId}`),

  // Rulesets
  listRulesets: () => api.get('/recon-rules/rulesets'),
  getRuleset: (rulesetId) => api.get(`/recon-rules/rulesets/${rulesetId}`),
  createRuleset: (data) => api.post('/recon-rules/rulesets', data),
  updateRuleset: (rulesetId, data) => api.put(`/recon-rules/rulesets/${rulesetId}`, data),
  deleteRuleset: (rulesetId) => api.delete(`/recon-rules/rulesets/${rulesetId}`),
  updateRulesetRules: (rulesetId, rules) => api.put(`/recon-rules/rulesets/${rulesetId}/rules`, { rules }),

  // Processes
  listProcesses: () => api.get('/recon-rules/processes'),
  getProcess: (processId) => api.get(`/recon-rules/processes/${processId}`),
  createProcess: (data) => api.post('/recon-rules/processes', data),
  updateProcess: (processId, data) => api.put(`/recon-rules/processes/${processId}`, data),
  deleteProcess: (processId) => api.delete(`/recon-rules/processes/${processId}`),
  updateProcessRulesets: (processId, rulesets) => api.put(`/recon-rules/processes/${processId}/rulesets`, { rulesets }),

  // Execution
  execute: (processId) => api.post(`/recon-rules/execute/${processId}`, {}, { timeout: 300000 }),
  listExecutions: (filters) => api.get('/recon-rules/executions', { params: filters }),
  getExecution: (runId) => api.get(`/recon-rules/executions/${runId}`),

  // Lookups
  getRuleTypes: () => api.get('/recon-rules/lookups/rule-types'),
  getPartners: () => api.get('/recon-rules/lookups/partners'),
};

export default api;
